import cn from "@/v2-ui/utils/utils.cn"
import { errorToast } from "@/v2-ui/toast"
import ManualLink from "@/v2-backoffice-client/components/manual/ManualLink"
import type { ManualKey } from "@/v2-backoffice-client/components/manual/manual.constants"
import Page, { type PageProps } from "@/v2-backoffice-client/components/page"
import Tabs, { useTabParam } from "@/v2-backoffice-client/components/tabs"
import UserTable from "@/v2-backoffice-client/user/UserTable"
import UserCreateManyButton from "@/v2-backoffice-client/user/create/many/UserCreateManyButton"
import UserCustomerMismatchTable from "@/v2-backoffice-client/user/customerMismatch/UserCustomerMismatchTable"
import UserCustomerMismatchTabTrigger from "@/v2-backoffice-client/user/customerMismatch/UserCustomerMismatchTabTrigger"
import UserEmailDuplicateTable from "@/v2-backoffice-client/user/emailDuplicate/UserEmailDuplicateTable"
import UserEmailDuplicateTabTrigger from "@/v2-backoffice-client/user/emailDuplicate/UserEmailDuplicateTabTrigger"
import { useUserStatusCountsQuery } from "@/v2-backoffice-client/user/counts/user.counts.query"
import {
  USER_TABS_ID,
  USER_VIEW_ALL,
  USER_VIEW_CUSTOMER_MISMATCHES,
  USER_VIEW_EMAIL_DUPLICATES,
} from "@/v2-backoffice-client/user/user.constants"

// Per view: what the board is for (behind the title's info toggle) and the
// manual that explains it (a header action while the tab is active).
const VIEWS: Record<string, { about: string; manual?: ManualKey }> = {
  [USER_VIEW_ALL]: {
    about:
      "Every user, with the customer that owns them and the teams they sit in.",
  },
  [USER_VIEW_CUSTOMER_MISMATCHES]: {
    about:
      "Users who sit in a team that belongs to another customer than the one that owns them. Every row has two ways out, side by side under Put Right: move the user to where the team belongs, or move the team (every member) to where the user belongs; the filled button is the way the team's other members point. A customer struck through with an arrow was merged away in HubSpot, and both sides then belong on the one after the arrow. Intended sharing between two companies is rare and is simply left as it is.",
    manual: "customerMismatches",
  },
  [USER_VIEW_EMAIL_DUPLICATES]: {
    about:
      "Emails more than one live user holds: one person with two rows, usually a Fusion login created in the Backoffice next to a B2C sign-up through an invitation (the sign-up and Backoffice guards now stop that). Keep merges the other rows of the email into the one you keep: teams, named seats, the login it lacks and the HubSpot link follow, and the merged rows are removed. The filled button is the row the person signed in with.",
    manual: "duplicateEmails",
  },
}

export type UserPageProps = PageProps

function UserPage(props: UserPageProps) {
  const { className, ...pageProps } = props

  const [activeView] = useTabParam(USER_TABS_ID, USER_VIEW_ALL)
  const view = VIEWS[activeView] ?? VIEWS[USER_VIEW_ALL]

  const { data, loading, error } = useUserStatusCountsQuery()
  const counts = data?.getUserStatusCounts
  const statuses = data?.getUserStatuses ?? []

  errorToast(error, {
    title: "Failed to query for user status counts",
  })

  return (
    <Page className={cn("user-page", className)} {...pageProps}>
      <Page.Header>
        <Page.Title about={view.about}>Users</Page.Title>
        <Page.Header.Actions>
          {view.manual && <ManualLink manual={view.manual} />}
          <UserCreateManyButton />
        </Page.Header.Actions>
      </Page.Header>

      <Page.Content className="flex-1 min-h-0 flex flex-col">
        <Tabs id={USER_TABS_ID} defaultValue={USER_VIEW_ALL}>
          <Tabs.List>
            <Tabs.Trigger value={USER_VIEW_ALL} isLoading={loading}>
              All Users
            </Tabs.Trigger>
            <UserCustomerMismatchTabTrigger />
            <UserEmailDuplicateTabTrigger />
          </Tabs.List>

          <Tabs.Content value={USER_VIEW_ALL} className="min-h-0">
            <UserTable
              counts={counts}
              statuses={statuses}
              statusLoading={loading}
            />
          </Tabs.Content>

          <Tabs.Content
            value={USER_VIEW_CUSTOMER_MISMATCHES}
            className="min-h-0"
          >
            <UserCustomerMismatchTable />
          </Tabs.Content>

          <Tabs.Content value={USER_VIEW_EMAIL_DUPLICATES} className="min-h-0">
            <UserEmailDuplicateTable />
          </Tabs.Content>
        </Tabs>
      </Page.Content>
    </Page>
  )
}

export default UserPage
